"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BoundaryMapperUtil = exports.BoundaryMapperUtilMergeMode = void 0;
const BoundaryCharacteristic_1 = require("../BoundaryCharacteristic");
const BoundaryClass_1 = require("../DesignModels/BoundaryClass");
const BoundaryLevel_1 = require("../BoundaryLevel");
const BoundaryTarget_1 = require("../BoundaryTarget");
const BoundaryAssertionType_1 = require("../DesignModels/BoundaryAssertionType");
const INumericalBoundaryDefinition_1 = require("../DesignModels/INumericalBoundaryDefinition");
const Utils_1 = require("../Utils");
var BoundaryMapperUtilMergeMode;
(function (BoundaryMapperUtilMergeMode) {
    BoundaryMapperUtilMergeMode[BoundaryMapperUtilMergeMode["Merge"] = 0] = "Merge";
    BoundaryMapperUtilMergeMode[BoundaryMapperUtilMergeMode["Replace"] = 1] = "Replace";
})(BoundaryMapperUtilMergeMode || (exports.BoundaryMapperUtilMergeMode = BoundaryMapperUtilMergeMode = {}));
class BoundaryMapperUtil {
    static getAmrBoundaries(signals, boundaries) {
        return Array.from(this.getAmrBoundaryEntries(signals, boundaries).values()).flat();
    }
    static getAmrBoundaryEntries(signals, boundaries) {
        return new Map(boundaries.map((b) => [b, this.toHilAmrBoundary(signals, b)]).filter(([, b]) => !!b?.length));
    }
    static mergeAmrBoundaries(signals, originalBoundaries, newBoundaries, mode) {
        if (mode === BoundaryMapperUtilMergeMode.Replace) {
            originalBoundaries = this.withoutAmrBoundaries(signals, originalBoundaries);
        }
        return originalBoundaries.concat(newBoundaries.flatMap((b) => this.mintAmrBoundaries(b)));
    }
    static mintAmrBoundaries(boundary) {
        return this.fromHilAmrBoundary(boundary);
    }
    static withoutAmrBoundaries(signals, boundaries) {
        const amrEntries = this.getAmrBoundaryEntries(signals, boundaries);
        return boundaries.filter((b) => !amrEntries.has(b));
    }
    static toHilAmrBoundary(signals, boundary) {
        if (!(0, INumericalBoundaryDefinition_1.isBoundaryNumericalDefinition)(boundary.boundary)) {
            return null;
        }
        const numerical = boundary.boundary.numerical;
        if (boundary.boundary.class !== BoundaryClass_1.BoundaryClass.IC_AbsoluteMaximumRating) {
            return null;
        }
        if (!(0, BoundaryTarget_1.isBoundaryTargetRelativeSignals)(boundary.boundary.target)) {
            return null;
        }
        return (0, Utils_1.toArray)(boundary.boundary.target.signal).map((signalId) => {
            const relativeSignal = (0, BoundaryTarget_1.getBoundaryTargetSignalIds)(signals, boundary.boundary.relative_to)[0] ?? null;
            switch (numerical.type) {
                case 'minimum': {
                    return {
                        signalId,
                        characteristic: numerical.characteristic,
                        minimum: numerical.value?.toString(),
                        minimumRelativeToSignalId: relativeSignal,
                        maximum: null,
                        maximumRelativeToSignalId: null,
                    };
                }
                case 'maximum': {
                    return {
                        signalId,
                        characteristic: numerical.characteristic,
                        minimum: null,
                        minimumRelativeToSignalId: null,
                        maximum: numerical.value?.toString(),
                        maximumRelativeToSignalId: relativeSignal,
                    };
                }
                case 'range': {
                    return {
                        signalId,
                        characteristic: numerical.characteristic,
                        minimum: numerical.minVal?.toString(),
                        minimumRelativeToSignalId: relativeSignal,
                        maximum: numerical.maxVal?.toString(),
                        maximumRelativeToSignalId: relativeSignal,
                    };
                }
                case 'absolute': {
                    return {
                        signalId,
                        characteristic: numerical.characteristic,
                        minimum: null,
                        minimumRelativeToSignalId: null,
                        maximum: null,
                        maximumRelativeToSignalId: null,
                    };
                }
            }
        });
    }
    static fromHilAmrBoundary(boundary) {
        const numerical = this.buildNumericalBoundary(boundary);
        return Array.from(numerical.entries()).flatMap(([relativeSignalId, numerical]) => {
            if (!numerical) {
                return [];
            }
            return {
                id: 'amr_' + (0, Utils_1.randomAlphaStr)(16),
                global: true,
                boundary: {
                    type: BoundaryAssertionType_1.BoundaryAssertionType.Numerical,
                    level: BoundaryLevel_1.BoundaryLevel.Error,
                    class: BoundaryClass_1.BoundaryClass.IC_AbsoluteMaximumRating,
                    target: {
                        signal: boundary.signalId,
                    },
                    relative_to: typeof relativeSignalId !== undefined ? { signal: relativeSignalId } : undefined,
                    numerical,
                },
            };
        });
    }
    static safeParseNumericalSIValue(value, characteristic) {
        try {
            const unit = BoundaryCharacteristic_1.BoundaryCharacteristicSIDefinitions[characteristic];
            const result = (0, Utils_1.parseNumericalSIValue)(value, unit);
            return result ?? parseFloat(value);
        }
        catch (error) {
            console.warn(`Failed to parse boundary value "${value}" for ${characteristic}:`, error instanceof Error ? error.message : error);
            return parseFloat(value);
        }
    }
    static buildNumericalBoundary(boundary) {
        const relativeSignals = new Set([
            boundary.minimumRelativeToSignalId,
            boundary.maximumRelativeToSignalId,
            boundary.nominalRelativeToSignalId,
        ].filter((s) => typeof s === 'string' || typeof s === 'number'));
        return new Map(Array.from(relativeSignals).map((signalId) => {
            const base = {
                characteristic: boundary.characteristic,
                nominalValue: boundary.nominal ? this.safeParseNumericalSIValue(boundary.nominal, boundary.characteristic) : undefined,
            };
            if (typeof boundary.minimum === 'string' &&
                boundary.minimum !== '' &&
                boundary.minimumRelativeToSignalId === signalId &&
                typeof boundary.maximum === 'string' &&
                boundary.maximum !== '' &&
                boundary.maximumRelativeToSignalId === signalId) {
                return [
                    signalId,
                    {
                        type: 'range',
                        minVal: this.safeParseNumericalSIValue(boundary.minimum, boundary.characteristic),
                        maxVal: this.safeParseNumericalSIValue(boundary.maximum, boundary.characteristic),
                        ...base,
                    },
                ];
            }
            if (typeof boundary.minimum === 'string' &&
                boundary.minimum !== '' &&
                boundary.minimumRelativeToSignalId === signalId) {
                return [
                    signalId,
                    {
                        type: 'minimum',
                        value: this.safeParseNumericalSIValue(boundary.minimum, boundary.characteristic),
                        ...base,
                    },
                ];
            }
            if (typeof boundary.maximum === 'string' &&
                boundary.maximum !== '' &&
                boundary.maximumRelativeToSignalId === signalId) {
                return [
                    signalId,
                    {
                        type: 'maximum',
                        value: this.safeParseNumericalSIValue(boundary.maximum, boundary.characteristic),
                        ...base,
                    },
                ];
            }
            if (typeof boundary.nominal === 'string' &&
                boundary.nominal !== '' &&
                boundary.nominalRelativeToSignalId === signalId) {
                return [
                    signalId,
                    {
                        type: 'absolute',
                        value: this.safeParseNumericalSIValue(boundary.nominal, boundary.characteristic),
                        ...base,
                    },
                ];
            }
            return [signalId, null];
        }));
    }
    static getOperatingConditionBoundaries(signals, boundaries) {
        return Array.from(this.getOperatingConditionBoundaryEntries(signals, boundaries).values()).flat();
    }
    static getOperatingConditionBoundaryEntries(signals, boundaries) {
        return new Map(boundaries
            .map((b) => [b, this.toHilOperatingConditionBoundary(signals, b)])
            .filter(([, b]) => !!b));
    }
    static toHilOperatingConditionBoundary(signals, boundary) {
        if (!(0, INumericalBoundaryDefinition_1.isBoundaryNumericalDefinition)(boundary.boundary)) {
            return null;
        }
        const numerical = boundary.boundary.numerical;
        if (boundary.boundary.class !== BoundaryClass_1.BoundaryClass.IC_ElectricalCharacteristics) {
            return null;
        }
        if (!(0, BoundaryTarget_1.isBoundaryTargetRelativeSignals)(boundary.boundary.target)) {
            return null;
        }
        return (0, Utils_1.toArray)(boundary.boundary.target.signal).map((signalId) => {
            const relativeSignal = (0, BoundaryTarget_1.getBoundaryTargetSignalIds)(signals, boundary.boundary.relative_to)[0] ?? null;
            switch (numerical.type) {
                case 'minimum': {
                    return {
                        signalId,
                        characteristic: numerical.characteristic,
                        minimum: numerical.value?.toString(),
                        minimumRelativeToSignalId: relativeSignal,
                        nominal: numerical.nominalValue?.toString() ?? null,
                        nominalRelativeToSignalId: relativeSignal,
                        maximum: null,
                        maximumRelativeToSignalId: null,
                    };
                }
                case 'maximum': {
                    return {
                        signalId,
                        characteristic: numerical.characteristic,
                        minimum: null,
                        minimumRelativeToSignalId: null,
                        nominal: numerical.nominalValue?.toString() ?? null,
                        nominalRelativeToSignalId: relativeSignal,
                        maximum: numerical.value?.toString(),
                        maximumRelativeToSignalId: relativeSignal,
                    };
                }
                case 'range': {
                    return {
                        signalId,
                        characteristic: numerical.characteristic,
                        minimum: numerical.minVal?.toString(),
                        minimumRelativeToSignalId: relativeSignal,
                        nominal: numerical.nominalValue?.toString() ?? null,
                        nominalRelativeToSignalId: relativeSignal,
                        maximum: numerical.maxVal?.toString(),
                        maximumRelativeToSignalId: relativeSignal,
                    };
                }
                case 'absolute': {
                    return {
                        signalId,
                        characteristic: numerical.characteristic,
                        minimum: null,
                        minimumRelativeToSignalId: null,
                        nominal: numerical.value?.toString(),
                        nominalRelativeToSignalId: relativeSignal,
                        maximum: null,
                        maximumRelativeToSignalId: null,
                    };
                }
            }
        });
    }
    static fromHilOperatingConditionBoundary(boundary, modes) {
        const numerical = this.buildNumericalBoundary(boundary);
        let conditions = {
            global: true,
        };
        if (modes.length > 0) {
            conditions = {
                conditions: modes.map((m) => ({
                    mode: m.id,
                    mode_state: true,
                })),
            };
        }
        return Array.from(numerical.entries()).flatMap(([relativeSignalId, numerical]) => {
            if (!numerical) {
                return [];
            }
            return {
                ...conditions,
                id: 'iec_' + (0, Utils_1.randomAlphaStr)(16),
                boundary: {
                    type: BoundaryAssertionType_1.BoundaryAssertionType.Numerical,
                    level: BoundaryLevel_1.BoundaryLevel.Warning,
                    class: BoundaryClass_1.BoundaryClass.IC_ElectricalCharacteristics,
                    target: {
                        signal: boundary.signalId,
                    },
                    relative_to: typeof relativeSignalId !== undefined ? { signal: relativeSignalId } : undefined,
                    numerical,
                },
            };
        });
    }
    static mergeOperatingConditionBoundaries(signals, originalBoundaries, newBoundaries, modes, mode) {
        if (mode === BoundaryMapperUtilMergeMode.Replace) {
            originalBoundaries = this.withoutOperatingConditionBoundaries(signals, originalBoundaries);
        }
        return originalBoundaries.concat(newBoundaries.flatMap((b) => this.mintOperatingConditionBoundaries(b, modes)));
    }
    static mintOperatingConditionBoundaries(boundary, modes) {
        return this.fromHilOperatingConditionBoundary(boundary, modes);
    }
    static withoutOperatingConditionBoundaries(signals, boundaries) {
        const ocEntries = this.getOperatingConditionBoundaryEntries(signals, boundaries);
        return boundaries.filter((b) => !ocEntries.has(b));
    }
}
exports.BoundaryMapperUtil = BoundaryMapperUtil;
//# sourceMappingURL=BoundaryMapperUtil.js.map